"use client";

import { ChevronRight } from "lucide-react";
import { useRef, useState } from "react";
import { useIsClampedOverflow } from "@/components/ui/clamped-text.client";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

/**
 * A pack's sales blurb, clamped to two lines with a highlight-card-style
 * "Read more" when it actually overflows.
 *
 * Lives inside the sale card's link, so the opener stops propagation — the
 * card itself already navigates to the pack page, and this is the short path
 * to the same words for someone who doesn't want to leave the list.
 */
export function PackSaleDescription({
  packName,
  description,
}: {
  packName: string;
  description: string;
}) {
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const isTruncated = useIsClampedOverflow(textRef, description);
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="relative px-5 pb-1">
        <p
          ref={textRef}
          className="line-clamp-2 text-[12.5px] leading-snug text-text-secondary"
        >
          {description}
        </p>
        {isTruncated ? (
          <button
            type="button"
            aria-label={`Read full description of ${packName}`}
            onClick={(event) => {
              // Inside the card's link: open the drawer, not the pack page.
              event.preventDefault();
              event.stopPropagation();
              setOpen(true);
            }}
            className="absolute right-4 bottom-0 inline-flex items-center gap-0.5 rounded-full border border-white/45 bg-white/55 py-0.5 pr-2 pl-2.5 text-[11px] font-semibold text-text-primary shadow-[inset_0_1px_0_0_rgba(255,255,255,0.55),0_1px_2px_rgba(15,23,42,0.06)] backdrop-blur-md backdrop-saturate-150 dark:border-white/14 dark:bg-white/[0.08] dark:text-white dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_1px_2px_rgba(0,0,0,0.35)]"
          >
            Read more
            <ChevronRight className="h-3 w-3 opacity-80" />
          </button>
        ) : null}
      </div>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="mx-auto max-h-[85vh] max-w-[640px]">
          <DrawerHeader className="flex flex-row items-center justify-between border-b border-border px-5 py-4 text-left dark:border-white/[0.1]">
            <DrawerTitle className="text-xl leading-tight font-semibold text-text-primary dark:text-white">
              {packName}
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-5 py-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
            <p className="text-[15px] leading-relaxed whitespace-pre-line text-text-secondary dark:text-white/80">
              {description}
            </p>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
